// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * DSA 1.8 vector playback (notes §6): which features are on the globe at
 * each rendered frame, and how far through their animations they are.
 *
 * The data time is continuous: T = timeRange.start + media seconds × fps ×
 * cadence. While playing forward, features whose time the playhead crosses
 * enter, play `enter`, hold, play `exit` and leave. Otherwise the layer is a
 * still **snapshot** of the playhead's frame, so a seek, a scrub, the first
 * frame and a loop wrap all show what the matching video frame shows.
 *
 * Choices this player makes where the notes leave room:
 * - **Pause freezes** the picture where it is (§6 allows it) instead of
 *   letting presentation-clock animations finish; play resumes them. A seek
 *   while paused shows the snapshot.
 * - **Sync is not a seek:** a media-time step within one frame
 *   (1 / videoFrameRate s) of where playback should be is a nudge. Forward
 *   nudges enter what they cross; backward ones change nothing, and a feature
 *   enters at most once between seeks.
 * - **maxActive** (a hint) keeps the features with the highest zOrder
 *   *value* when too many would enter at once.
 * - **Ripple** rings start seconds/rings apart and each expands over
 *   `seconds`, overlapping, as AYNI 3D draws them.
 * - **A track's head** (and its label) shows only while the storm is
 *   tracked: for one more fix interval after its last fix (the track's own
 *   spacing, at most one frame), as the video's disc marks only active
 *   storms. §7.6 leaves it open; this is the pipeline's intent (notes §10,
 *   2026-10-12). With yearly frames (IBTrACS since 1950) a whole frame kept
 *   every storm of the year headed and labelled (2026-10-13). The line stays
 *   for its exit.
 */
import type { CompiledVectorLayer, VectorFeature } from './vectorModel'
import { ease, evalNumber, matches } from './vectorStyle'
import type { DsaClockFields, VectorAnimation, VectorEmphasis } from './vectorTypes'

/** The data clock of §6. */
export interface VectorClock {
  /** timeRange.start, Unix seconds. */
  startS: number
  frames: number
  fps: number
  /** Data seconds per frame. */
  cadence: number
}

export function vectorClockOf(dsa: DsaClockFields): VectorClock | null {
  const startS = Date.parse(dsa.timeRange?.start ?? '') / 1000
  const frames = dsa.timeTotalFrames ?? 0
  const fps = dsa.videoFrameRate ?? 0
  const cadence = dsa.timeCadenceSeconds ?? 0
  if (dsa.timeEnabled === false || !Number.isFinite(startS) || frames <= 0 || fps <= 0 || cadence <= 0) return null
  return { startS, frames, fps, cadence }
}

/** Data time (Unix s) at a media time (s). */
export function dataTimeAt(clock: VectorClock, mediaSeconds: number): number {
  return clock.startS + mediaSeconds * clock.fps * clock.cadence
}

/** End of the last frame: the timeline's data end. */
export function timelineEnd(clock: VectorClock): number {
  return clock.startS + clock.frames * clock.cadence
}

/** End (exclusive) of the frame that holds data time T. */
export function frameEndAt(clock: VectorClock, T: number): number {
  const i = Math.min(clock.frames - 1, Math.max(0, Math.floor((T - clock.startS) / clock.cadence)))
  return clock.startS + (i + 1) * clock.cadence
}

export interface VectorTickInput {
  /** Media seconds: the video's currentTime, or the player's own clock. */
  mediaTime: number
  /** Playing forward (rate > 0, not paused, not scrubbing). */
  playing: boolean
  /** Playback rate, for telling sync nudges from seeks. Default 1. */
  rate?: number
  /** Presentation seconds since the last tick: wall time live, 1 / outputFps in renders. */
  dt: number
  /** The playhead is held at the end of the timeline (T = last frame's end). */
  atEnd?: boolean
  /** A discontinuity the caller knows of (dataset load, user seek), whatever its size. */
  seek?: boolean
}

export interface EmphasisDraw {
  effect: 'ripple' | 'pulse' | 'halo'
  emphasis: VectorEmphasis
  /** ripple / halo: size multiple of each ring and its opacity. */
  rings: Array<{ scale: number; opacity: number }>
}

export interface VectorDrawItem {
  feature: VectorFeature
  /** 0..1 multiplier on size / line width (grow, shrink, pulse folded in). */
  size: number
  /** 0..1 animation opacity. */
  opacity: number
  /** 0..1 of each line / outline drawn (the `draw` effect). */
  draw: number
  /** 0..1 polygon fill (the `draw` effect fills after the outline). */
  fill: number
  emphasis: EmphasisDraw[]
  /** Tracks: data time the line is drawn up to. */
  headT: number
  /** Tracks: whether the head (and its label) is drawn — false once the track has ended. */
  head: boolean
}

interface Timing {
  enterDelay: number
  enterSecs: number
  exitDelay: number
  exitSecs: number
  emphasis: Array<{ e: VectorEmphasis; secs: number }>
}

interface Active {
  f: VectorFeature
  timing: Timing
  /** Shown as part of a snapshot: no enter animation, no emphasis. */
  held: boolean
  enterP: number
  enterD: number
  exitP?: number
  exitD?: number
}

const ENTER_EFFECTS = new Set(['none', 'grow', 'fade', 'grow-fade', 'draw'])
const EXIT_EFFECTS = new Set(['none', 'fade', 'shrink', 'shrink-fade'])
const EMPHASIS_EFFECTS = new Set(['ripple', 'pulse', 'halo'])

function lowerBound(sorted: VectorFeature[], key: (f: VectorFeature) => number, x: number): number {
  let lo = 0
  let hi = sorted.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (key(sorted[mid]) < x) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** Index of the first item with key > x. */
function upperBound(sorted: VectorFeature[], key: (f: VectorFeature) => number, x: number): number {
  let lo = 0
  let hi = sorted.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (key(sorted[mid]) <= x) lo = mid + 1
    else hi = mid
  }
  return lo
}

const firstVertex = (f: VectorFeature) => f.vertexTimes![0]
const lastVertex = (f: VectorFeature) => f.vertexTimes![f.vertexTimes!.length - 1]

export class VectorTimeline {
  private readonly mode: string
  private readonly byKey: VectorFeature[]
  private readonly key: (f: VectorFeature) => number
  private readonly timings = new Map<number, Timing>()
  private readonly active = new Map<number, Active>()
  private readonly entered = new Set<number>()
  private state: 'snapshot' | 'live' | 'frozen' = 'snapshot'
  private first = true
  private lastMedia = 0
  private lastT = 0
  /** Presentation clock: advances only while live (or always, for static layers). */
  private nowP = 0
  private nowD = 0
  private snapshotItems: VectorDrawItem[] = []

  constructor(readonly layer: CompiledVectorLayer, readonly clock: VectorClock | null) {
    this.mode = layer.spec.time.mode
    this.key = this.mode === 'instant' ? (f) => f.time! : this.mode === 'interval' ? (f) => f.start! : this.mode === 'vertex' ? firstVertex : () => 0
    this.byKey = [...layer.features].sort((a, b) => this.key(a) - this.key(b))
  }

  /** Per-feature durations and the emphasis that applies, evaluated once. */
  private timing(f: VectorFeature): Timing {
    let t = this.timings.get(f.index)
    if (!t) {
      const { enter, exit, emphasis } = this.layer.spec.style
      const n = (v: VectorAnimation['seconds'], d: number) => Math.max(0, evalNumber(v, f.attrs, d) ?? d)
      t = {
        enterDelay: n(enter?.delaySeconds, 0), enterSecs: enter ? n(enter.seconds, 0.25) : 0,
        exitDelay: n(exit?.delaySeconds, 0), exitSecs: exit ? n(exit.seconds, 0.25) : 0,
        emphasis: (emphasis ?? []).filter((e) => EMPHASIS_EFFECTS.has(e.effect) && matches(e.when, f.attrs))
          .map((e) => ({ e, secs: Math.max(0, evalNumber(e.seconds, f.attrs, 1.5) ?? 1.5) })),
      }
      this.timings.set(f.index, t)
    }
    return t
  }

  /** The data time the timeline is at. */
  get dataTime(): number {
    return this.nowD
  }

  /** Features drawn now, with their animation state. */
  tick(input: VectorTickInput): VectorDrawItem[] {
    const dt = Math.max(0, input.dt)
    if (this.mode === 'static') return this.tickStatic(dt)
    if (!this.clock) return []
    const fps = this.clock.fps
    const T = input.atEnd ? timelineEnd(this.clock) : dataTimeAt(this.clock, input.mediaTime)
    const dm = input.mediaTime - this.lastMedia
    const frame = 1 / fps
    let seek = this.first || !!input.seek
    if (!seek && !input.atEnd) {
      if (input.playing && this.state === 'live') {
        const expected = (input.rate ?? 1) * dt
        seek = dm < -frame || dm - expected > frame
      } else {
        seek = Math.abs(dm) >= frame
      }
    }
    this.first = false
    this.lastMedia = input.mediaTime
    this.nowD = T

    if (seek) {
      this.active.clear()
      this.entered.clear()
      this.state = 'snapshot'
    }
    if (!input.playing) {
      if (this.state === 'live') this.state = 'frozen'
      if (this.state === 'frozen') return this.draw()
      this.snapshotItems = this.snapshot(T)
      this.lastT = T
      return this.snapshotItems
    }
    if (this.state === 'snapshot') {
      // §6: the active set is rebuilt from the snapshot, then features enter normally.
      this.rebuildFromSnapshot(T)
      this.state = 'live'
      this.lastT = T
      return this.draw()
    }
    this.state = 'live'
    this.nowP += dt
    const T0 = this.lastT
    if (T > T0) this.advance(T0, T, dt)
    this.lastT = T
    return this.draw()
  }

  private tickStatic(dt: number): VectorDrawItem[] {
    if (this.first) {
      this.first = false
      for (const f of this.byKey) {
        this.active.set(f.index, { f, timing: this.timing(f), held: false, enterP: 0, enterD: 0 })
      }
    }
    this.nowP += dt
    return this.draw()
  }

  /**
   * The features of the frame holding T, as a paused globe draws them (its
   * snapshot): instants in the frame's window, intervals and tracks under
   * way. Also what the chatbot reads as "shown" (`features.query`).
   */
  snapshotFeatures(T: number): VectorFeature[] {
    const clock = this.clock!
    if (this.mode === 'instant') {
      const end = frameEndAt(clock, T)
      const win = this.layer.spec.time.snapshotWindowSeconds ?? clock.cadence
      return this.byKey.slice(lowerBound(this.byKey, this.key, end - win), lowerBound(this.byKey, this.key, end))
    }
    if (this.mode === 'interval') {
      return this.byKey.slice(0, upperBound(this.byKey, this.key, T)).filter((f) => T < f.end!)
    }
    // vertex: tracks drawn up to T. A finished track stays while its exit
    // is still being held on the data clock, then goes.
    const exit = this.layer.spec.style.exit
    return this.byKey.slice(0, upperBound(this.byKey, this.key, T)).filter((f) => {
      const hold = exit?.clock === 'data' ? this.timing(f).exitDelay : 0
      return T <= lastVertex(f) + hold
    })
  }

  private snapshot(T: number): VectorDrawItem[] {
    return this.snapshotFeatures(T).map((f) => ({
      feature: f, size: 1, opacity: 1, draw: 1, fill: 1, emphasis: [], headT: T, head: this.headOn(f, T),
    }))
  }

  /** A track's head is drawn from its first fix to the end of the frame holding its last. */
  private headOn(f: VectorFeature, T: number): boolean {
    if (this.mode !== 'vertex' || !f.vertexTimes?.length) return true
    const times = f.vertexTimes
    const frame = this.clock?.cadence ?? 0
    const spacing = times.length > 1 ? times[times.length - 1] - times[times.length - 2] : frame
    return T <= lastVertex(f) + Math.min(frame, spacing > 0 ? spacing : frame)
  }

  private rebuildFromSnapshot(T: number): void {
    for (const f of this.snapshotFeatures(T)) {
      const a: Active = { f, timing: this.timing(f), held: true, enterP: this.nowP, enterD: T }
      // An instant feature from the snapshot has entered already: its hold
      // and exit start now. Intervals and tracks leave by their own times.
      if (this.mode === 'instant') { a.exitP = this.nowP; a.exitD = T }
      this.active.set(f.index, a)
      this.entered.add(f.index)
    }
  }

  /**
   * Forward from T0 (exclusive) to T1 (inclusive), over `dt` presentation
   * seconds. Each crossing is dated where it falls inside the step, so a
   * big step (a slow device, a Studio render frame) keeps the animations
   * where live playback would have them.
   */
  private advance(T0: number, T1: number, dt: number): void {
    const at = (t: number) => this.nowP - dt * Math.min(1, Math.max(0, (T1 - t) / (T1 - T0)))
    const from = upperBound(this.byKey, this.key, T0)
    const to = upperBound(this.byKey, this.key, T1)
    const crossing = this.limit(this.byKey.slice(from, to).filter((f) => !this.entered.has(f.index)))
    for (const f of crossing) {
      const t = this.key(f)
      this.entered.add(f.index)
      this.active.set(f.index, { f, timing: this.timing(f), held: false, enterP: at(t), enterD: t })
    }
    for (const a of this.active.values()) {
      if (a.exitP !== undefined) continue
      const leaveAt = this.mode === 'interval' ? a.f.end! : this.mode === 'vertex' ? lastVertex(a.f) : NaN
      if (leaveAt <= T1) { a.exitP = at(leaveAt); a.exitD = leaveAt }
    }
  }

  /**
   * §6 maxActive: keep the highest zOrder values when too many would
   * animate. Features held from a snapshot do not count: they never grow.
   */
  private limit(crossing: VectorFeature[]): VectorFeature[] {
    const max = this.layer.spec.style.maxActive
    const animating = [...this.active.values()].filter((a) => !a.held).length
    if (!max || animating + crossing.length <= max) return crossing
    const room = Math.max(0, max - animating)
    const z = this.layer.spec.style.zOrder?.field
    const value = (f: VectorFeature) => (z && typeof f.attrs[z] === 'number' ? (f.attrs[z] as number) : 0)
    const keep = new Set([...crossing].sort((a, b) => value(b) - value(a)).slice(0, room))
    for (const f of crossing) if (!keep.has(f)) this.entered.add(f.index)
    return crossing.filter((f) => keep.has(f))
  }

  private elapsed(anim: VectorAnimation | VectorEmphasis | undefined, p: number, d: number): number {
    return anim?.clock === 'data' ? this.nowD - d : this.nowP - p
  }

  private draw(): VectorDrawItem[] {
    const { enter, exit } = this.layer.spec.style
    const enterFx = enter && ENTER_EFFECTS.has(enter.effect) ? enter.effect : 'none'
    const exitFx = exit && EXIT_EFFECTS.has(exit.effect) ? exit.effect : 'none'
    const out: VectorDrawItem[] = []
    for (const [key, a] of this.active) {
      const t = a.timing
      const item: VectorDrawItem = { feature: a.f, size: 1, opacity: 1, draw: 1, fill: 1, emphasis: [], headT: this.nowD, head: this.headOn(a.f, this.nowD) }
      if (!a.held) {
        const e = this.elapsed(enter, a.enterP, a.enterD) - t.enterDelay
        const x = t.enterSecs > 0 ? Math.max(0, e) / t.enterSecs : e >= 0 ? 1 : 0
        const p = ease(enter?.easing, x)
        if (e < 0) { item.size = 0; item.opacity = 0; item.draw = 0; item.fill = 0 }
        else if (enterFx === 'grow') item.size = p
        else if (enterFx === 'fade') item.opacity = p
        else if (enterFx === 'grow-fade') { item.size = p; item.opacity = p }
        else if (enterFx === 'draw') {
          const polygon = this.layer.spec.geometry === 'polygon'
          item.draw = polygon ? Math.min(1, p * 2) : p
          item.fill = polygon ? Math.max(0, p * 2 - 1) : 1
        }
        // Instant features start their exit as their enter ends.
        if (this.mode === 'instant' && a.exitP === undefined && x >= 1) {
          if (enter?.clock === 'data') { a.exitD = a.enterD + t.enterDelay + t.enterSecs; a.exitP = this.nowP }
          else { a.exitP = a.enterP + t.enterDelay + t.enterSecs; a.exitD = this.nowD }
        }
        item.emphasis = this.emphasis(a)
        for (const em of item.emphasis) if (em.effect === 'pulse') item.size *= em.rings[0]?.scale ?? 1
      }
      if (a.exitP !== undefined) {
        const e = this.elapsed(exit, a.exitP, a.exitD!) - t.exitDelay
        if (e >= t.exitSecs) { this.active.delete(key); continue }
        if (e > 0) {
          const p = 1 - ease(exit?.easing, e / t.exitSecs)
          if (exitFx === 'fade') item.opacity *= p
          else if (exitFx === 'shrink') item.size *= p
          else if (exitFx === 'shrink-fade') { item.size *= p; item.opacity *= p }
          else { this.active.delete(key); continue }
        }
      }
      out.push(item)
    }
    return out
  }

  /** §7.5, played once as the feature enters. */
  private emphasis(a: Active): EmphasisDraw[] {
    const out: EmphasisDraw[] = []
    for (const { e, secs } of a.timing.emphasis) {
      const t = this.elapsed(e, a.enterP, a.enterD)
      const rings = Math.max(1, Math.min(5, e.rings ?? 2))
      const scale = e.scale ?? 2.5
      const slot = secs / rings
      if (secs <= 0 || t < 0) continue
      if (e.effect === 'ripple') {
        // Ring k starts k × seconds/rings in and expands over `seconds`, so
        // the rings overlap, as AYNI 3D draws them (§7.5 leaves it open).
        const list: EmphasisDraw['rings'] = []
        for (let k = 0; k < rings; k++) {
          const x = (t - k * slot) / secs
          if (x >= 0 && x < 1) list.push({ scale: 1 + (scale - 1) * x, opacity: 1 - x })
        }
        if (list.length) out.push({ effect: 'ripple', emphasis: e, rings: list })
        continue
      }
      if (t >= secs) continue
      if (e.effect === 'pulse') {
        const x = (t % slot) / slot
        out.push({ effect: 'pulse', emphasis: e, rings: [{ scale: 1 + (scale - 1) * Math.sin(Math.PI * x), opacity: 1 }] })
      } else {
        out.push({ effect: 'halo', emphasis: e, rings: [{ scale, opacity: 1 - t / secs }] })
      }
    }
    return out
  }
}
